import React, { useState, lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { Bars3Icon } from '@heroicons/react/24/outline';
import Sidebar from "./components/Sidebar";
import ErrorBoundary from "./components/ErrorBoundary";
import Preloader from "./components/Preloader";

// Lazy load route components for code splitting
const Homepage = lazy(() => import("./components/Homepage"));
const Exchanges = lazy(() => import("./components/Exchanges"));
const Cryptos = lazy(() => import("./components/Cryptos"));
const CryptoDetails = lazy(() => import("./components/CryptoDetails"));
const News = lazy(() => import("./components/News"));

const App = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="min-h-screen bg-crypto-darker dark:bg-crypto-darker bg-gray-50 transition-colors duration-300">
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onCollapse={setSidebarCollapsed}
      />

      {/* Main content wrapper */}
      <div className={`transition-all duration-300 min-h-screen flex flex-col ${sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'}`}>
        {/* Top bar (mobile) */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center gap-x-6 bg-crypto-card dark:bg-crypto-card bg-white border-b border-gray-800 dark:border-gray-800 border-gray-200 px-4 py-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-crypto-cardHover transition-colors"
            aria-label="Open navigation menu"
          >
            <Bars3Icon className="w-6 h-6 text-gray-300" />
          </button>
          <div className="flex-1 text-sm font-semibold text-gray-900 dark:text-white">
            Info Crypto
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <ErrorBoundary>
              <Suspense fallback={<Preloader />}>
                <Routes>
                  <Route path="/" element={<Homepage />} />
                  <Route path="exchanges" element={<Exchanges />} />
                  <Route path="cryptos" element={<Cryptos />} />
                  <Route path="crypto/:coinId" element={<CryptoDetails />} />
                  <Route path="news" element={<News />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white dark:bg-crypto-card border-t border-gray-200 dark:border-gray-800 mt-auto">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div className="text-center md:text-left">
                <p className="text-gray-800 dark:text-gray-300 font-semibold mb-1">Crypto Info</p>
                <p className="text-gray-700 dark:text-gray-400 text-sm">
                  {new Date().getFullYear()} React is fun :)
                </p>
                <p className="text-gray-700 dark:text-gray-400 text-sm">
                  created by{" "}
                  <a
                    href="https://www.linkedin.com/in/albert-64/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors"
                  >
                    Albert
                  </a>
                </p>
              </div>
              <div className="text-xs text-gray-700 dark:text-gray-400">
                <p>Powered by RapidAPI</p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default App;
